import React from 'react';
import { ElevationPoint, ElevationMarker } from '../../types';
import { TrendingUp, Tent, Mountain, Coffee, Landmark, Zap, LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { BRAND } from '../../constants/mapColors';
interface ElevationProfileProps {
  profile?: ElevationPoint[];
  markers?: ElevationMarker[];
  onMarkerClick?: (markerId: string) => void;
  startLabel?: string;
  endLabel?: string;
}

const MARKER_COLORS: Record<ElevationMarker['category'], string> = {
  wildspot: BRAND,
  campsite: '#1565C0',
  rest: '#3b82f6',
  attraction: '#8b5cf6',
  charger: '#eab308',
};

const MARKER_ICONS: Record<ElevationMarker['category'], LucideIcon> = {
  wildspot: Tent,
  campsite: Mountain,
  rest: Coffee,
  attraction: Landmark,
  charger: Zap,
};

const ElevationProfile: React.FC<ElevationProfileProps> = ({ profile, markers, onMarkerClick, startLabel, endLabel }) => {
  const { t } = useTranslation();
  if (!profile || profile.length < 2) return null;
  if (!profile.some(p => p.elevation > 0)) return null;

  const width = 300;
  const height = 80;
  const padding = 10;

  const maxDist = profile[profile.length - 1].distance;
  const elevations = profile.map(p => p.elevation);
  const minEle = Math.min(...elevations);
  const maxEle = Math.max(...elevations);
  const eleRange = maxEle - minEle || 1;

  let gain = 0;
  for (let i = 1; i < profile.length; i++) {
    const diff = profile[i].elevation - profile[i - 1].elevation;
    if (diff > 0) gain += diff;
  }

  let maxPointIndex = 0;
  elevations.forEach((ele, idx) => {
    if (ele > elevations[maxPointIndex]) maxPointIndex = idx;
  });
  const peakPoint = profile[maxPointIndex];

  const points = profile.map(p => {
    const x = (p.distance / maxDist) * width;
    const y = height - ((p.elevation - minEle) / eleRange) * (height - 2 * padding) - padding;
    return `${x},${y}`;
  }).join(' ');

  const peakX = (peakPoint.distance / maxDist) * width;
  const peakY = height - ((peakPoint.elevation - minEle) / eleRange) * (height - 2 * padding) - padding;

  const fillD = `M ${points} L ${width},${height} L 0,${height} Z`;

  // Deduplicate markers that are too close on screen (percentage-based)
  const filteredMarkers = markers ? markers.reduce<ElevationMarker[]>((acc, m) => {
    const mxPct = maxDist > 0 ? (m.distanceKm / maxDist) * 100 : 0;
    const tooClose = acc.some(existing => {
      const existPct = (existing.distanceKm / maxDist) * 100;
      return Math.abs(existPct - mxPct) < 2.5;
    });
    if (!tooClose) acc.push(m);
    return acc;
  }, []) : [];

  // Compute marker positions as percentages for HTML overlay
  const markerPositions = filteredMarkers.map((marker) => {
    const leftPct = maxDist > 0 ? (marker.distanceKm / maxDist) * 100 : 0;
    const closestProfile = profile.reduce((prev, curr) =>
      Math.abs(curr.distance - marker.distanceKm) < Math.abs(prev.distance - marker.distanceKm)
        ? curr : prev
    );
    const yInSvg = height - ((closestProfile.elevation - minEle) / eleRange) * (height - 2 * padding) - padding;
    const topPct = (yInSvg / height) * 100;
    return { marker, leftPct, topPct };
  });

  // Peak position as percentage
  const peakLeftPct = maxDist > 0 ? (peakX / width) * 100 : 0;
  const peakTopPct = (peakY / height) * 100;

  return (
    <div className="bg-[var(--bg-surface-alt)] p-4 rounded-3xl border border-[var(--border-primary)] space-y-3">
      <h3 className="text-xs font-black text-[var(--text-muted)] uppercase tracking-widest ml-1 flex items-center justify-between">
        <span className="flex items-center gap-2"><TrendingUp size={14} /> {t('route.elevation.title')}</span>
        <span className="text-emerald-400 font-bold">{t('route.elevation.total_gain')}: +{Math.round(gain)}m</span>
      </h3>

      <div className="relative w-full" style={{ height: '6rem' }}>
        {/* SVG for elevation line and fill only */}
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="elevationGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={BRAND} stopOpacity="0.35" />
              <stop offset="100%" stopColor={BRAND} stopOpacity="0.02" />
            </linearGradient>
          </defs>
          <path d={fillD} fill="url(#elevationGradient)" stroke="none" />
          <polyline points={points} fill="none" stroke={BRAND} strokeWidth="1.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
        </svg>

        {/* Peak label as HTML (avoids SVG distortion) */}
        {maxEle > 600 && (
          <div
            className="absolute -translate-x-1/2 pointer-events-none flex flex-col items-center"
            style={{ left: `${peakLeftPct}%`, top: `${peakTopPct}%`, transform: 'translate(-50%, -100%)' }}
          >
            <span className="text-3xs font-black text-amber-400 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] whitespace-nowrap">
              {Math.round(maxEle)}m
            </span>
            <div className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-sm shadow-black/50 mt-0.5" />
          </div>
        )}

        {/* POI markers as HTML overlay — no SVG distortion */}
        {markerPositions.map(({ marker, leftPct, topPct }) => {
          const color = MARKER_COLORS[marker.category] || BRAND;
          const Icon = MARKER_ICONS[marker.category] || Tent;

          return (
            <div
              key={marker.id}
              className="absolute -translate-x-1/2 -translate-y-1/2 group"
              style={{ left: `${leftPct}%`, top: `${topPct}%`, cursor: onMarkerClick ? 'pointer' : undefined }}
              onClick={(e) => {
                e.stopPropagation();
                onMarkerClick?.(marker.id);
              }}
            >
              <div
                className="w-[18px] h-[18px] rounded-full flex items-center justify-center shadow-md ring-[1.5px] ring-white/40 transition-transform group-hover:scale-125"
                style={{ backgroundColor: color }}
              >
                <Icon size={9} className="text-white" strokeWidth={2.5} />
              </div>
              {marker.label && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 bg-black/80 rounded text-4xs text-white whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none backdrop-blur-sm max-w-[120px] truncate">
                  {marker.label}
                </div>
              )}
            </div>
          );
        })}

        {/* Min/Max labels */}
        <div className="absolute top-1 right-1 text-[7px] font-bold text-[var(--text-muted)] bg-[var(--bg-input)] px-1.5 py-0.5 rounded-md backdrop-blur-sm">
          {Math.round(maxEle)}m
        </div>
        <div className="absolute bottom-1 right-1 text-[7px] font-bold text-[var(--text-muted)] bg-[var(--bg-input)] px-1.5 py-0.5 rounded-md backdrop-blur-sm">
          {Math.round(minEle)}m
        </div>
      </div>

      {/* Start / End place labels */}
      {(startLabel || endLabel) && (
        <div className="flex items-center justify-between px-1 -mt-1">
          <span className="text-3xs text-[var(--text-muted)] font-medium truncate max-w-[40%]">{startLabel}</span>
          {startLabel && endLabel && <div className="flex-1 border-t border-dashed border-[var(--border-secondary)] mx-3" />}
          <span className="text-3xs text-[var(--text-muted)] font-medium truncate max-w-[40%] text-right">{endLabel}</span>
        </div>
      )}
    </div>
  );
};

export default ElevationProfile;
